import { usePrivy } from "@privy-io/react-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { env } from "@/utils/envs";
import QueryKey from "@/utils/queryKeys";

// --- Types ---

export const questStatusSchema = z.enum(["draft", "active", "archived"]);
export type QuestStatus = z.infer<typeof questStatusSchema>;

// Helper to convert NaN to null for optional number fields
const optionalNumber = z.union([
	z.nan().transform(() => null),
	z.number().min(1),
	z.null(),
	z.undefined(),
]);

export const questStepSchema = z.object({
	id: z.string().optional(),
	name: z.string().min(1, "Step name is required"),
	description: z.string().optional().nullable(),
	eventName: z.string().min(1, "Event name is required"),
	requiredCount: z.number().min(1, "Required count must be at least 1"),
	withinSeconds: optionalNumber,
	eventPropsFilter: z
		.record(z.string(), z.union([z.string(), z.null(), z.undefined()]))
		.optional()
		.nullable(),
});

export const questFormSchema = z.object({
	id: z.string().optional().nullable(),
	name: z.string().min(1, "Challenge name is required"),
	description: z.string().optional().nullable(),
	imageUrl: z.string().optional().nullable(),
	status: questStatusSchema,
	points: z.number().min(0, "Points must be non-negative"),
	startAt: z.string().optional().nullable(),
	endAt: z.string().optional().nullable(),
	maxCompletionsPerUser: optionalNumber,
	steps: z.array(questStepSchema),
});

export type QuestFormData = z.infer<typeof questFormSchema>;
export type QuestStepData = z.infer<typeof questStepSchema>;

export type RewardsQuest = {
	id: string;
	createdByUserId: string;
	name: string;
	description: string | null;
	imageUrl: string | null;
	status: QuestStatus;
	points: number;
	startAt: string | null;
	endAt: string | null;
	maxCompletionsPerUser: number | null;
	createdAt: string;
	updatedAt: string;
};

export type RewardsQuestStep = {
	id: string;
	questId: string;
	name: string;
	description: string | null;
	eventName: string | null;
	requiredCount: number;
	withinSeconds: number | null;
	eventPropsFilter: Record<string, string> | null;
};

export type QuestLifecycle = "active" | "upcoming" | "expired";

export type RewardsQuestDetails = {
	quest: RewardsQuest;
	steps: RewardsQuestStep[];
	publishStatus?: QuestStatus;
	lifecycle?: QuestLifecycle;
};

// --- API Functions ---

const createFetchWithAuth = (getAccessToken: () => Promise<string | null>) => {
	return async (path: string, options?: RequestInit) => {
		const token = await getAccessToken();
		if (!token) {
			throw new Error("Missing access token");
		}

		const response = await fetch(`${env.VITE_SHINKAI_WEB_API_URL}${path}`, {
			...options,
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
				...(options?.headers ?? {}),
			},
		});

		if (!response.ok) {
			const data = (await response.json().catch(() => ({}))) as {
				error?: string;
				message?: string;
			};
			throw new Error(data.error || data.message || "Request failed");
		}

		return response.json();
	};
};

// --- Hooks ---

export const useRewardsQuests = () => {
	const { authenticated, getAccessToken } = usePrivy();

	return useQuery({
		queryKey: [QueryKey.REWARDS_QUESTS],
		queryFn: async () => {
			const fetchWithAuth = createFetchWithAuth(getAccessToken);
			// scope=all requires admin auth; returns drafts, active, and archived
			const data = (await fetchWithAuth("/api/rewards/quests?scope=all")) as {
				quests: RewardsQuestDetails[];
				appliedFilters?: Record<string, string>;
			};
			return data.quests ?? [];
		},
		enabled: authenticated,
		staleTime: 30_000,
	});
};

export const useCreateQuest = () => {
	const { getAccessToken } = usePrivy();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: QuestFormData) => {
			const fetchWithAuth = createFetchWithAuth(getAccessToken);
			const payload = buildQuestPayload(data);
			return fetchWithAuth("/api/rewards/quests", {
				method: "POST",
				body: JSON.stringify(payload),
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QueryKey.REWARDS_QUESTS] });
		},
		onError: (error) => {
			console.error("Error creating quest:", error);
			toast;
		},
	});
};

export const useUpdateQuest = () => {
	const { getAccessToken } = usePrivy();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: QuestFormData }) => {
			const fetchWithAuth = createFetchWithAuth(getAccessToken);
			const payload = buildQuestPayload(data);
			return fetchWithAuth(`/api/rewards/quests/${id}`, {
				method: "PATCH",
				body: JSON.stringify(payload),
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QueryKey.REWARDS_QUESTS] });
		},
		onError: (error) => {
			console.error("Error updating quest:", error);
			toast.error("Failed to update challenge.", {
				description:
					error instanceof Error ? error.message : "Something went wrong",
			});
		},
	});
};

export const useDeleteQuest = () => {
	const { getAccessToken } = usePrivy();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (questId: string) => {
			const fetchWithAuth = createFetchWithAuth(getAccessToken);
			return fetchWithAuth(`/api/rewards/quests/${questId}`, {
				method: "DELETE",
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QueryKey.REWARDS_QUESTS] });
		},
		onError: (error) => {
			const message =
				error instanceof Error ? error.message : "Something went wrong";
			toast.error(`Failed to delete challenge: ${message}`);
		},
	});
};

// --- Image Upload ---

export type ImageUploadResponse = {
	uploadUrl: string;
	headers: Record<string, string>;
	objectPath: string;
	publicUrl: string;
};

/**
 * Hook that handles the full quest image upload flow:
 * 1. Requests a signed upload URL from the backend.
 * 2. PUTs the file directly to the signed URL.
 * 3. Returns the `publicUrl` to be stored as the quest's `imageUrl`.
 */
export const useQuestImageUpload = () => {
	const { getAccessToken } = usePrivy();

	return useMutation({
		mutationFn: async ({
			file,
			questId,
		}: {
			file: File;
			questId?: string;
		}): Promise<ImageUploadResponse> => {
			const fetchWithAuth = createFetchWithAuth(getAccessToken);

			// Step 1: Get the signed upload URL
			const uploadData = (await fetchWithAuth(
				"/api/rewards/quests/image-upload-url",
				{
					method: "POST",
					body: JSON.stringify({
						contentType: file.type,
						fileSize: file.size,
						filename: file.name,
						questId: questId ?? undefined,
					}),
				},
			)) as ImageUploadResponse;

			// Step 2: PUT the file directly to the signed URL
			const uploadResponse = await fetch(uploadData.uploadUrl, {
				method: "PUT",
				headers: uploadData.headers,
				body: file,
			});

			if (!uploadResponse.ok) {
				throw new Error(
					`Image upload failed (${uploadResponse.status}): ${uploadResponse.statusText}`,
				);
			}

			return uploadData;
		},
		onError: (error) => {
			const message = error instanceof Error ? error.message : "Upload failed";
			toast.error("Image upload failed.", {
				description: message,
			});
		},
	});
};

// --- Helpers ---

const buildQuestPayload = (form: QuestFormData) => {
	const stepsPayload = form.steps.map((step) => {
		let cleanedFilter: Record<string, string> | null = null;
		if (step.eventPropsFilter) {
			const entries = Object.entries(step.eventPropsFilter).filter(
				(entry): entry is [string, string] =>
					typeof entry[1] === "string" && entry[1].trim() !== "",
			);
			if (entries.length > 0) {
				cleanedFilter = Object.fromEntries(entries);
			}
		}

		return {
			id: step.id ?? undefined,
			name: step.name,
			description: step.description || null,
			eventName: step.eventName,
			requiredCount: step.requiredCount,
			withinSeconds: step.withinSeconds ?? undefined,
			eventPropsFilter: cleanedFilter,
		};
	});

	return {
		name: form.name,
		description: form.description || null,
		...(form.imageUrl ? { imageUrl: form.imageUrl } : {}),
		status: form.status,
		points: form.points,
		startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
		endAt: form.endAt ? new Date(form.endAt).toISOString() : null,
		maxCompletionsPerUser: form.maxCompletionsPerUser ?? null,
		steps: stepsPayload,
	};
};

export const questDetailsToFormData = (
	details: RewardsQuestDetails,
): QuestFormData => {
	return {
		id: details.quest.id,
		name: details.quest.name,
		description: details.quest.description,
		imageUrl: details.quest.imageUrl,
		status: details.quest.status,
		points: details.quest.points,
		startAt: toInputDateTime(details.quest.startAt),
		endAt: toInputDateTime(details.quest.endAt),
		maxCompletionsPerUser: details.quest.maxCompletionsPerUser,
		steps: details.steps.map((step) => ({
			id: step.id,
			name: step.name,
			description: step.description,
			eventName: step.eventName ?? "",
			requiredCount: step.requiredCount ?? 1,
			withinSeconds: step.withinSeconds,
			eventPropsFilter: step.eventPropsFilter ?? {},
		})),
	};
};

export const createEmptyQuestForm = (): QuestFormData => ({
	name: "",
	description: "",
	imageUrl: null,
	status: "draft",
	points: 0,
	startAt: "",
	endAt: "",
	maxCompletionsPerUser: null,
	steps: [],
});

const toInputDateTime = (value: string | null): string => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toISOString().slice(0, 16);
};

export const formatDisplayDate = (value: string | null): string => {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
};
